'use client';
import { useState, useEffect, useCallback } from 'react';
import { FiUser, FiMail, FiPhone, FiX, FiSend, FiShield, FiShare2 } from 'react-icons/fi';
import Image from 'next/image';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  provider?: string;
  is_verified?: boolean;
}

interface ProfileSectionProps {
  user: User;
}

export default function ProfileSection({ user }: ProfileSectionProps) {

  // Profile sharing states
  const [whatsappMessage, setWhatsappMessage] = useState('');

  const [hasMutualTransferApplication, setHasMutualTransferApplication] = useState(false);

  // WhatsApp OTP states
  // Re-enabled 2 Aug 2026: the WABA restriction cleared, OTPs deliver again —
  // the Verify Phone button is back.
  const PHONE_OTP_ENABLED = true;
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Load profile sharing data on component mount
  const checkMutualTransferApplication = useCallback(async () => {
    try {
      const response = await fetch('/api/bihar/mutual-transfer', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        setHasMutualTransferApplication(true);
        // Load profile share data only if user has an application
        // eslint-disable-next-line react-hooks/immutability -- calls stable helper; no props/state mutated
        await loadProfileShareData();
      } else if (response.status === 404) {
        setHasMutualTransferApplication(false);
      }
    } catch (error) {
      console.error('Error checking mutual transfer application:', error);
      setHasMutualTransferApplication(false);
    }
  }, []);

  // Load profile sharing data on component mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial load; effect performs async work
    checkMutualTransferApplication();
  }, [checkMutualTransferApplication]);

  const loadProfileShareData = async () => {
    try {
      const response = await fetch('/api/bihar/mutual-transfer/share', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setWhatsappMessage(data.whatsappMessage);
      }
    } catch (error) {
      console.error('Error loading profile share data:', error);
    }
  };

  const handleShareWhatsApp = () => {
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Phone number validation function
  const validatePhoneNumber = (phone: string) => {
    // Check if it's a valid Indian mobile number format
    if (!/^[6-9]\d{9}$/.test(phone)) {
      return { valid: false, error: 'Please enter a valid 10-digit Indian mobile number' };
    }
    
    // Check for common invalid patterns
    if (phone.startsWith('000') || phone.startsWith('999') || phone.startsWith('888')) {
      return { valid: false, error: 'This number pattern is not valid' };
    }
    
    // Check for sequential numbers
    if (/^(\d)\1{8}$/.test(phone)) {
      return { valid: false, error: 'This number pattern is not valid' };
    }
    
    return { valid: true };
  };

  // WhatsApp OTP functions
  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      setOtpError('Please enter a valid 10-digit mobile number');
      return;
    }

    // Additional validation to prevent invalid numbers
    const phoneValidation = validatePhoneNumber(phoneNumber);
    if (!phoneValidation.valid) {
      setOtpError(phoneValidation.error || 'Invalid phone number');
      return;
    }

    setIsSendingOtp(true);
    setOtpError('');

    try {
      const response = await fetch('/api/auth/whatsapp/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: `+91${phoneNumber}`,
          name: user?.name || 'User',
          email: user?.email
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      setOtpSent(true);
      setResendTimer(60); // 60 seconds countdown
      setOtpError('');
    } catch (error) {
      console.error('Send OTP error:', error);
      setOtpError(error instanceof Error ? error.message : 'Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      const response = await fetch('/api/auth/verify-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: `+91${phoneNumber}`,
          otp: otp
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify OTP');
      }

      // Update user verification status
      setOtpSent(false);
      setOtp('');
      setOtpError('');
      setShowPhoneVerification(false);
      
      // Update the mutual transfer application's mobile field if it exists
      try {
        const updateResponse = await fetch('/api/bihar/mutual-transfer/update-mobile', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mobile: phoneNumber }),
          credentials: 'include'
        });
        
        if (!updateResponse.ok && updateResponse.status !== 404) {
          console.error('Failed to update application mobile field');
        } else if (updateResponse.ok) {
          console.log('Application mobile field updated successfully');
        }
      } catch (error) {
        console.error('Error updating application mobile field:', error);
      }
      
      // Refresh the page to get updated user data
      window.location.reload();
    } catch (error) {
      console.error('Verify OTP error:', error);
      setOtpError(error instanceof Error ? error.message : 'Failed to verify OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Profile</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 text-2xl sm:text-3xl">
              {user.avatar_url ? (
                <Image 
                  src={user.avatar_url} 
                  alt={user.name}
                  width={80}
                  height={80}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
                />
              ) : (
                <FiUser />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">{user.name}</h2>
              <p className="text-slate-500 text-sm sm:text-base truncate">{user.email}</p>
              {user.is_verified && (
                <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                  Verified
                </span>
              )}
            </div>
          </div>
          
          {/* Share Profile Button */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {hasMutualTransferApplication && (
              <>
                {/* <button
                  onClick={handleCopyProfileUrl}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs sm:text-sm"
                  title="Copy profile link"
                >
                  <FiCopy size={12} className="sm:w-3.5 sm:h-3.5" />
                  <span className="hidden sm:inline">Copy Link</span>
                  <span className="sm:hidden">Copy</span>
                </button> */}
                <button
                  onClick={handleShareWhatsApp}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm"
                  title="Share on WhatsApp"
                >
                  <FiShare2 size={12} className="sm:w-3.5 sm:h-3.5" />
                  <span className="hidden sm:inline">Share</span>
                  <span className="sm:hidden">Share</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FiUser className="text-slate-500" size={20} />
            <div className="flex-1">
              <label className="block text-sm text-slate-600 mb-1">Name</label>
              <p className="text-slate-900">{user.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <FiMail className="text-slate-500" size={20} />
            <div className="flex-1">
              <label className="block text-sm text-slate-600 mb-1">Email</label>
              <p className="text-slate-900">{user.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <FiPhone className="text-slate-500" size={20} />
            <div className="flex-1">
              <label className="block text-sm text-slate-600 mb-1">Phone</label>
              <div className="flex items-center gap-3">
                <p className="text-slate-900">{user.phone || 'Not provided'}</p>
                {PHONE_OTP_ENABLED && !user.is_verified && (
                  <button
                    onClick={() => setShowPhoneVerification(true)}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <FiShield size={14} />
                    Verify Phone
                  </button>
                )}
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Account Information */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-slate-600 text-sm">Account Type</p>
            <p className="text-slate-900">{user.provider || 'Email'}</p>
          </div>
          <div>
            <p className="text-slate-600 text-sm">Verification Status</p>
            <p className="text-slate-900">{user.is_verified ? 'Verified' : 'Not Verified'}</p>
          </div>
          <div>
            <p className="text-slate-600 text-sm">Member Since</p>
            <p className="text-slate-900">{new Date().toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-slate-600 text-sm">Last Updated</p>
            <p className="text-slate-900">{new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>



      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Verify Phone Number</h3>
              <button
                onClick={() => {
                  setShowPhoneVerification(false);
                  setOtpSent(false);
                  setOtp('');
                  setOtpError('');
                  setPhoneNumber('');
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                <FiX size={20} />
              </button>
            </div>

            {!otpSent ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Phone Number</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 py-2 bg-slate-100 border border-r-0 border-slate-300 rounded-l-lg text-slate-600">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit mobile number"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-r-lg focus:outline-none focus:border-blue-500"
                      maxLength={10}
                    />
                  </div>
                </div>

                {otpError && (
                  <p className="text-red-600 text-sm">{otpError}</p>
                )}

                <button
                  onClick={handleSendOtp}
                  disabled={isSendingOtp || phoneNumber.length !== 10}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {isSendingOtp ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiSend size={16} />
                      Send OTP via WhatsApp
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Enter OTP</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 text-center text-lg tracking-widest"
                    maxLength={6}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    OTP sent to +91{phoneNumber}
                  </p>
                </div>

                {otpError && (
                  <p className="text-red-600 text-sm">{otpError}</p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleVerifyOtp}
                    disabled={isVerifyingOtp || otp.length !== 6}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                  >
                    {isVerifyingOtp ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FiShield size={16} />
                        Verify OTP
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0}
                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                  >
                    {resendTimer > 0 ? `${resendTimer}s` : 'Resend'}
                  </button>
                </div>

                <button
                  onClick={() => {
                    setOtpSent(false);
                    setOtp('');
                    setOtpError('');
                  }}
                  className="w-full px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Change Phone Number
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
